import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Grid,
  IconButton,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
// eslint-disable-next-line import/no-unresolved
import { startCase } from "lodash";
import { useDemoCommercialApplication } from "@/hooks";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import { computeServiceability, toHemShareCarried, toHemSplitOff, toStoredShareFraction } from "@/utils/serviceability";
import {
  ERRORS_MESSAGE,
  MARITAL_STATUSES,
  SERVICEABILITY_COMMENT,
  SHARE_SLIDER_MARKS,
  SHARES_TYPES,
} from "@/constants";
import {
  resetExpensesAndLiabilitiesShares,
  saveCustomerProfile,
  saveValue,
  userDetailsSelector,
} from "@/store/slices/applicationFormSlice";
import { customerSelector } from "@/store/slices/customerSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import CommentBox from "@components/customComponents/CommentBox";
import ConfirmModal from "@components/customComponents/ConfirmModal";
// eslint-disable-next-line import/no-unresolved
import Assets from "./Assets";
import Liabilities from "./Liabilities";
import Income from "./Income";
import Expenses from "./Expenses";
import ServiceabilityBreakdown from "./ServiceabilityBreakdown";

const normalizeExpenseOrLiabilitySharePercent = (raw) => {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 100;
};

/**
 * shareOfExpenses is stored as a fraction of 1, but older records saved it as a percentage (0-100).
 * Anything above 1 is therefore a legacy percentage and is brought back onto the slider's scale.
 */
const toShareOfExpensesFraction = (stored, fallbackFraction) => {
  // Number(null) and Number("") are both 0, so an unset share has to be caught before the numeric check,
  // otherwise a profile without one reads as a 0% share instead of falling back to the full amount.
  if (stored == null || stored === "") return fallbackFraction;
  const n = Number(stored);
  if (!Number.isFinite(n)) return fallbackFraction;
  return toStoredShareFraction(Math.min(1, Math.max(0, n > 1 ? n / 100 : n)));
};

/**
 * The stored HEM split as a number, or null when it has never been set. Coercing here keeps the
 * comparisons against local state numeric, so an API returning "0.5" is not read as an unsaved edit.
 */
const toStoredHemShare = (stored) => {
  // Number("") is 0, so an unset field saved as an empty string has to be caught alongside null: read as an
  // explicit 0% carried share it would drop the whole HEM floor and overstate the surplus.
  if (stored == null || stored === "") return null;
  const n = Number(stored);
  return Number.isFinite(n) ? toStoredShareFraction(n) : null;
};

/**
 * Both share sliders run on a 0-1 scale because that is how the values are stored, but every label around them —
 * the marks, the captions and the tooltips — is written as a percentage, so the drag label is formatted to match
 * rather than showing the raw fraction.
 */
const formatSharePercent = (fraction) => `${Math.round(fraction * 100)}%`;

/** Rounds a surplus to the two decimals the customer profile stores. */
const toStoredSurplus = (value) => Number(value.toFixed(2));

/** True when every expense shareOfExpense and liability shareOfLiability is the same value (or fewer than 2 values exist). */
const expenseAndLiabilitySharesAllMatch = (expenses, liabilities) => {
  const percents = [
    ...(expenses ?? []).map((e) => normalizeExpenseOrLiabilitySharePercent(e.shareOfExpense)),
    ...(liabilities ?? []).map((l) => normalizeExpenseOrLiabilitySharePercent(l.shareOfLiability)),
  ];
  if (percents.length < 2) return true;
  const first = percents[0];
  return percents.every((p) => Math.abs(p - first) < 0.01);
};

const CustomerPersonalFinances = React.memo(
  ({ customer, customerId, applicationType, applicationId, customerIndex, isDisableEdit }) => {
    const dispatch = useDispatch();
    const { isDemoCommercialApplication } = useDemoCommercialApplication();

    const { personalFinLoaders, isConfirmingShareType } = useSelector(userDetailsSelector);
    const { isCustomerLogin = false } = useSelector(customerSelector);
    const { customerProfile } = customer;
    const [serviceAbility, setServiceAbility] = useState({
      sharedExpenses: toShareOfExpensesFraction(customerProfile?.shareOfExpenses, 1),
      sharesType: SHARES_TYPES.ALL,
      // null means "not set yet": the full HEM benchmark then applies.
      shareOfHem: toStoredHemShare(customerProfile?.shareOfHem),
    });
    const isDisabledSharesField = isDisableEdit || serviceAbility?.sharesType === SHARES_TYPES.ALL;
    const [shareTypeConfirmOpen, setShareTypeConfirmOpen] = useState(false);
    // Counts the HEM split drag/save cycles still in flight, so the surplus is saved once on commit rather than
    // on every step. A single boolean would be cleared by whichever cycle settles first: a broker who starts a
    // second drag before the first save resolves would have the first one's cleanup reopen the autosave and
    // race the second save. The count is only ever cleared by a cycle that settles, never by comparing against
    // the stored split, so a profile that does not echo shareOfHem back cannot latch the autosave off.
    const [pendingHemAdjustments, setPendingHemAdjustments] = useState(0);
    const isAdjustingHemSplit = pendingHemAdjustments > 0;
    // onChange fires on every step of a drag, so the cycle is counted once when the drag starts.
    const isDraggingHemRef = useRef(false);
    // The stored split the slider was last reconciled with — see the sync effect below.
    const syncedShareOfHemRef = useRef(undefined);
    // The first surplus computed for a customer is not written back — see the autosave effect below.
    const primedCustomerIdRef = useRef(null);

    useEffect(() => {
      if (!customer?.customerProfile) return;

      const expenses = customer.expenses ?? [];
      const liabilities = customer.liabilities ?? [];
      const allSharesMatch = expenseAndLiabilitySharesAllMatch(expenses, liabilities);

      setServiceAbility((prev) => ({
        ...prev,
        sharesType: allSharesMatch ? SHARES_TYPES.ALL : SHARES_TYPES.INDIVIDUALLY,
        sharedExpenses: toShareOfExpensesFraction(customer.customerProfile.shareOfExpenses, prev.sharedExpenses),
      }));
    }, [customer?._id]);

    // Keeps the HEM split in step with the stored value, including a profile that loads after the first render.
    // It is held while a drag or its save is in flight: a save resolving under the broker's cursor would
    // otherwise snap the slider back onto the value that just landed and discard the adjustment in progress.
    // Settling the last cycle re-runs this effect so the slider still reconciles, and the ref limits that
    // reconciliation to a stored value that has genuinely moved — a save the profile does not echo back
    // therefore leaves the committed split on screen instead of reverting it.
    useEffect(() => {
      if (isAdjustingHemSplit) return;

      const stored = toStoredHemShare(customerProfile?.shareOfHem);
      if (syncedShareOfHemRef.current === stored) return;

      syncedShareOfHemRef.current = stored;
      setServiceAbility((prev) => ({
        ...prev,
        shareOfHem: stored,
      }));
    }, [customer?._id, customerProfile?.shareOfHem, isAdjustingHemSplit]);

    const serviceabilityInput = useMemo(
      () => ({
        income: customer?.income,
        expenses: customer?.expenses,
        liabilities: customer?.liabilities,
        customerProfile,
      }),
      [customer?.income, customer?.expenses, customer?.liabilities, customerProfile],
    );

    const serviceability = useMemo(
      () => computeServiceability({ ...serviceabilityInput, shareOfHem: serviceAbility.shareOfHem }),
      [serviceabilityInput, serviceAbility.shareOfHem],
    );

    const { surplus } = serviceability;
    const storedShareOfHem = toStoredHemShare(customerProfile?.shareOfHem);

    const isMarried = customerProfile?.maritalStatus === MARITAL_STATUSES.MARRIED_DEFACTO;
    const hasMaritalStatus = Boolean(customerProfile?.maritalStatus);
    // Without a resolved benchmark there is nothing to split, so the slider cannot change serviceability.
    const isHemApplicable = serviceability.hemBenchmark !== null;

    /** The split applies to the single rates as well, so only the wording changes with the marital status. */
    const getHemSplitHelpText = () => {
      if (!isHemApplicable) {
        if (!hasMaritalStatus) {
          return (
            <React.Fragment>
              A HEM benchmark applies once a <strong>marital status</strong> is recorded for this applicant. Until then
              serviceability uses the stated household expenses and this split has no effect.
            </React.Fragment>
          );
        }

        return (
          <React.Fragment>
            No HEM benchmark matches this applicant&apos;s income, so serviceability uses the stated household expenses
            and this split has no effect.
          </React.Fragment>
        );
      }

      if (isMarried) {
        return (
          <React.Fragment>
            Sets how much of the household <strong>HEM</strong> benchmark is split off to the applicant&apos;s partner.
            It defaults to 0%, leaving the whole benchmark with this applicant, and should stay there wherever the
            partner&apos;s earnings are recorded as a <strong>Spouse</strong> income, since the benchmark then covers
            the household being assessed. Raise it only where the partner is not on the loan and their income has been
            left out, for example 50% where they cover the other half. It is applied separately from the expense and
            liability shares.
          </React.Fragment>
        );
      }

      return (
        <React.Fragment>
          The single <strong>HEM</strong> rate already covers one adult, so nothing is normally split off and this
          applicant carries all of it. Shared housing costs are captured through the share on the <strong>Rent</strong>{" "}
          expense, which sits outside the benchmark, so they do not need to be taken off here as well. It is applied
          separately from the expense and liability shares.
        </React.Fragment>
      );
    };

    const getHemSplitCaption = () => {
      if (!isHemApplicable) {
        return hasMaritalStatus
          ? "No HEM benchmark matches this income"
          : "Record a marital status to apply a HEM benchmark";
      }

      // Both figures are spelled out, so the split reads the same way whichever side of it the broker has in mind.
      const splitPercent = Math.round(toHemSplitOff(serviceability.hemShare) * 100);
      const applied = `${splitPercent}% split — applying ${100 - splitPercent}% of the HEM benchmark`;
      return serviceability.isHemShareDefault ? `${applied} (default)` : applied;
    };

    useEffect(() => {
      if (customer?.customerProfile == null) return;
      // While the HEM split is being dragged, the surplus is saved with it in one request once it is committed.
      if (isAdjustingHemSplit) return;

      // The first surplus computed for a customer is not written back. A stored figure was recorded under
      // whichever calculation was live at the time, and merely opening the page is not a reassessment: writing
      // it here would silently restate a serviceability decision with no broker action and no audit trail.
      // Every later change — an edited income, expense, liability or HEM split — is a reassessment and saves.
      if (primedCustomerIdRef.current !== customer._id) {
        primedCustomerIdRef.current = customer._id;
        return;
      }

      const saved = Number(customer?.customerProfile?.serviceabilitySurplus);
      const sameValue = Number.isFinite(saved) && Math.abs(saved - surplus) <= 0.01;
      if (sameValue) return;

      dispatch(
        saveCustomerProfile({
          data: {
            serviceabilitySurplus: toStoredSurplus(surplus),
            customer: customer._id,
          },
        }),
      );
    }, [surplus, isAdjustingHemSplit]);

    const handle = {
      toggleShareTypeConfirmModal: (value) => {
        setShareTypeConfirmOpen(value);
      },
      resetExpensesAndLiabilitiesShares: async (sharesValue) => {
        const res = await dispatch(
          resetExpensesAndLiabilitiesShares({
            applicationId,
            customerId,
            sharesValue: sharesValue ?? 100,
          }),
        ).unwrap();

        if (res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "error",
            }),
          );
          return null;
        }

        return res;
      },
      updateShareType: async (event) => {
        const value = event?.target?.value;
        if (value == null || serviceAbility?.sharesType === value) {
          return;
        }
        // Open confirm modal if shares type convert from individually to all
        if (value === SHARES_TYPES.INDIVIDUALLY) {
          setServiceAbility({
            ...serviceAbility,
            sharesType: value,
          });
        } else {
          handle.toggleShareTypeConfirmModal(true);
        }
      },
      confirmShareTypeChange: async (data) => {
        if (!data?.isSave) {
          handle.toggleShareTypeConfirmModal(false);
          return;
        }

        const res = await handle.resetExpensesAndLiabilitiesShares();
        if (res) {
          // The item shares are reset with a percentage, while shareOfExpenses is stored as a fraction of 1.
          await dispatch(
            saveCustomerProfile({
              data: {
                shareOfExpenses: 1,
                customer: customerId,
              },
            }),
          ).unwrap();
          handle.toggleShareTypeConfirmModal(false);
          // reset isConfirmingShareType to false when customer profile is saved
          dispatch(saveValue({ isConfirmingShareType: false }));
          setServiceAbility((prev) => ({
            ...prev,
            sharedExpenses: 1, // reset to 100%
            sharesType: SHARES_TYPES.ALL,
          }));
        }
      },
      shareOfExpenses: async (event, committedValue) => {
        // MUI computes a stepped value as `min + step * Math.round(...)`, so the 15% mark arrives as
        // 0.15000000000000002. Rounding to the stored precision keeps an unchanged slider comparing equal:
        // otherwise a no-op commit saves again and resets every per-item share to the same percentage.
        const value = toStoredShareFraction(committedValue);
        if (toShareOfExpensesFraction(customer?.customerProfile?.shareOfExpenses, null) === value) {
          return;
        }

        await dispatch(
          saveCustomerProfile({
            data: {
              shareOfExpenses: value,
              customer: customerId,
            },
          }),
        ).unwrap();
        // The item shares are reset with a percentage, while shareOfExpenses is stored as a fraction of 1.
        const res = await handle.resetExpensesAndLiabilitiesShares(value * 100);
        // reset isConfirmingShareType to false when customer profile is saved
        if (res) {
          dispatch(saveValue({ isConfirmingShareType: false }));
        }
      },
      onChangeSharedExpenses: (event) => {
        const value = parseFloat(event.target.value);
        setServiceAbility((prev) => ({
          ...prev,
          sharedExpenses: value,
        }));
      },
      onChangeShareOfHem: (event, splitOff) => {
        if (!isDraggingHemRef.current) {
          isDraggingHemRef.current = true;
          setPendingHemAdjustments((count) => count + 1);
        }
        setServiceAbility((prev) => ({
          ...prev,
          shareOfHem: toHemShareCarried(splitOff),
        }));
      },
      /** Clears this drag's hold on the surplus autosave, only if the drag was counted in the first place. */
      settleHemAdjustment: (wasCounted) => {
        if (!wasCounted) return;
        setPendingHemAdjustments((count) => Math.max(0, count - 1));
      },
      saveShareOfHem: async (event, splitOff) => {
        const wasCounted = isDraggingHemRef.current;
        isDraggingHemRef.current = false;

        const shareOfHem = toHemShareCarried(splitOff);
        if (storedShareOfHem === shareOfHem) {
          handle.settleHemAdjustment(wasCounted);
          return;
        }

        // The surplus is recomputed for the committed split and saved alongside it, so this stays a single request.
        const { surplus: committedSurplus } = computeServiceability({ ...serviceabilityInput, shareOfHem });

        try {
          await dispatch(
            saveCustomerProfile({
              data: {
                shareOfHem,
                serviceabilitySurplus: toStoredSurplus(committedSurplus),
                customer: customerId,
              },
            }),
          ).unwrap();
        } catch (error) {
          // Put the slider back on the stored value so it never shows a split that was not saved.
          setServiceAbility((prev) => ({
            ...prev,
            shareOfHem: storedShareOfHem,
          }));
          dispatch(
            openSnackbar({
              message: error?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "error",
            }),
          );
        } finally {
          handle.settleHemAdjustment(wasCounted);
        }
      },
      saveServiceabilityComment: async (comment) => {
        try {
          await dispatch(
            saveCustomerProfile({
              data: {
                [SERVICEABILITY_COMMENT.fieldName]: comment,
                customer: customerId,
              },
            }),
          ).unwrap();
        } catch (error) {
          // The field keeps the typed comment, so the broker can leave it again to retry the save.
          dispatch(
            openSnackbar({
              message: error?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "error",
            }),
          );
        }
      },
    };

    return (
      <React.Fragment>
        <Grid
          container
          item
          xs={12}
          style={{
            margin: "0 0 30px 0",
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            paddingBottom: "10px",
          }}
        >
          <Grid item md={12} sm={12}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Typography fontWeight={600}>Personal Finances</Typography>
            </div>
          </Grid>

          <Grid container item xs={12} mb={"24px"}>
            <Assets
              customerIndex={customerIndex}
              assets={customer.customerAssets}
              customerId={customerId}
              applicationId={applicationId}
              personalFinLoaders={personalFinLoaders}
              isDisableEdit={isDisableEdit}
            />
            <Liabilities
              liabilities={customer.liabilities}
              customerId={customerId}
              customerIndex={customerIndex}
              applicationId={applicationId}
              applicationType={applicationType}
              personalFinLoaders={personalFinLoaders}
              isDisableEdit={isDisableEdit}
              isDisabledSharesField={isDisabledSharesField}
            />
            {isDemoCommercialApplication && (
              <Income
                income={customer.income}
                customerId={customer._id}
                customerIndex={customerIndex}
                applicationId={applicationId}
                personalFinLoaders={personalFinLoaders}
                isDisableEdit={isDisableEdit}
              />
            )}

            {(applicationType === ApplicationType.Consumer || applicationType === ApplicationType.Personal) && (
              <React.Fragment>
                <Income
                  income={customer.income}
                  customerId={customerId}
                  customerIndex={customerIndex}
                  applicationId={applicationId}
                  personalFinLoaders={personalFinLoaders}
                  isDisableEdit={isDisableEdit}
                />

                <Expenses
                  expenses={customer.expenses}
                  customerId={customerId}
                  customerIndex={customerIndex}
                  applicationId={applicationId}
                  personalFinLoaders={personalFinLoaders}
                  isDisableEdit={isDisableEdit}
                  isDisabledSharesField={isDisabledSharesField}
                />
                <Grid container item xs={12} my={"24px"} rowGap={"24px"}>
                  <Box width={{ xs: "100%", md: "55%" }}>
                    <Typography mb={"8px"} fontWeight={700} fontSize={"14px"}>
                      Expense & liability share type
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ mb: "16px" }}>
                      <ToggleButtonGroup
                        exclusive
                        value={serviceAbility?.sharesType ?? SHARES_TYPES.ALL}
                        onChange={handle.updateShareType}
                        size="small"
                        color="secondary"
                      >
                        {Object.values(SHARES_TYPES).map((type) => (
                          <ToggleButton key={type} value={type}>
                            {startCase(type)}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                      {serviceAbility?.sharesType === SHARES_TYPES.INDIVIDUALLY && (
                        <Tooltip
                          title={
                            <Typography variant="caption" component="span" sx={{ display: "block", maxWidth: 360 }}>
                              Use <strong>Share of expense</strong> on each expense and{" "}
                              <strong>Share of liability</strong> on each liability. Repayments and expense amounts in
                              serviceability use those percentages (0–100%).
                            </Typography>
                          }
                          placement="top"
                          arrow
                          enterTouchDelay={0}
                        >
                          <IconButton
                            size="small"
                            color="secondary"
                            aria-label="How individual share percentages work"
                            edge="end"
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {serviceAbility?.sharesType === SHARES_TYPES.ALL && (
                      <React.Fragment>
                        <Typography mb={"12px"} fontWeight={600} fontSize={"13px"}>
                          Shared expenses (applies to totals)
                        </Typography>
                        <Box ml={"10px"}>
                          <Slider
                            required
                            color="secondary"
                            size="large"
                            track={false}
                            marks={SHARE_SLIDER_MARKS}
                            step={0.05}
                            min={0}
                            max={1}
                            aria-label="Share of expenses"
                            valueLabelDisplay="auto"
                            valueLabelFormat={formatSharePercent}
                            getAriaValueText={formatSharePercent}
                            disabled={isDisableEdit}
                            value={serviceAbility.sharedExpenses}
                            onChange={handle.onChangeSharedExpenses}
                            onChangeCommitted={handle.shareOfExpenses}
                          />
                        </Box>
                      </React.Fragment>
                    )}
                  </Box>

                  <Box width={{ xs: "100%", md: "55%" }}>
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: "4px" }}>
                      <Typography fontWeight={700} fontSize={"14px"}>
                        HEM split
                      </Typography>
                      <Tooltip
                        title={
                          <Typography variant="caption" component="span" sx={{ display: "block", maxWidth: 360 }}>
                            {getHemSplitHelpText()}
                          </Typography>
                        }
                        placement="top"
                        arrow
                        enterTouchDelay={0}
                      >
                        <IconButton size="small" color="secondary" aria-label="How the HEM split works" edge="end">
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Typography mb={"12px"} fontSize={"12px"} color="text.secondary">
                      {getHemSplitCaption()}
                    </Typography>
                    <Box ml={"10px"}>
                      <Slider
                        color="secondary"
                        size="large"
                        track={false}
                        disabled={!isHemApplicable || isDisableEdit}
                        marks={SHARE_SLIDER_MARKS}
                        step={0.05}
                        min={0}
                        max={1}
                        aria-label="HEM split off to another party"
                        valueLabelDisplay="auto"
                        valueLabelFormat={formatSharePercent}
                        getAriaValueText={formatSharePercent}
                        value={toHemSplitOff(serviceability.hemShare)}
                        onChange={handle.onChangeShareOfHem}
                        onChangeCommitted={handle.saveShareOfHem}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid container item xs={12} md={12} style={{ padding: "0 0 15px 0" }}>
                  <ServiceabilityBreakdown
                    serviceability={serviceability}
                    customerProfile={customerProfile}
                    isUsingIndividualShares={serviceAbility?.sharesType === SHARES_TYPES.INDIVIDUALLY}
                  />
                </Grid>
                <Grid container item xs={12} md={12}>
                  <Stack>
                    <Alert
                      severity="info"
                      sx={{
                        padding: "2px 8px",
                        fontSize: "0.8rem",
                        minHeight: "unset",
                        lineHeight: "1.2",
                        "& .MuiAlert-icon": {
                          fontSize: "1.2rem", // Reduce icon size
                        },
                      }}
                    >
                      The serviceability calculation does not include the loan repayments for this application.
                    </Alert>
                  </Stack>
                </Grid>
                {/* Broker commentary on the serviceability, so it stays out of the customer's own detail form. */}
                {!isCustomerLogin && (
                  <Grid item xs={12} mt={"24px"}>
                    <CommentBox
                      id={`${SERVICEABILITY_COMMENT.fieldName}-${customerId}`}
                      placeholder={SERVICEABILITY_COMMENT.placeholder}
                      maxLength={SERVICEABILITY_COMMENT.maxLength}
                      minRows={SERVICEABILITY_COMMENT.minRows}
                      value={customerProfile?.serviceabilityComment}
                      disabled={isDisableEdit}
                      onSave={handle.saveServiceabilityComment}
                    />
                  </Grid>
                )}
              </React.Fragment>
            )}
          </Grid>
        </Grid>
        {shareTypeConfirmOpen && (
          <ConfirmModal
            isOpen={shareTypeConfirmOpen}
            handle={handle.confirmShareTypeChange}
            setIsOpen={handle.toggleShareTypeConfirmModal}
            title="Change expense & liability share type?"
            message="Are you sure you want to change how expense and liability shares are applied?"
            additionalDetails={
              <Typography textAlign="center" fontSize={"12px"} sx={{ color: "warning.main", fontWeight: 600 }}>
                If you continue, all share of expense and share of liability values will be reset to 100%.
              </Typography>
            }
            btnTextYes="Continue"
            btnTextNo="Cancel"
            isConfirmLoading={isConfirmingShareType}
            confirmLoadingText="Saving..."
          />
        )}
      </React.Fragment>
    );
  },
);

export default CustomerPersonalFinances;
